import rescalert from 'assets/resc_placeholder.png';
import BaseMap from 'components/BaseMap/BaseMap';
import CustomSlider from 'components/Sliders/CustomSlider';
import { Alarm, AlarmInfo } from 'features/Alarms/types/Alarms.types';
import {
  alertColorScale,
  getAlarmIdentifier,
  getBoundsFromCoords,
  getSeverityLevel,
  parseKey,
  safeGetProp,
  withCanceledSuffix,
} from 'features/Alarms/utils/alarmUtils';
import 'leaflet/dist/leaflet.css';
import { useMemo } from 'react';
import { Marker, Polygon } from 'react-leaflet';
import normalizePolygon from 'utils/maps/geometry.utils';
import { imageToIcon } from 'utils/devices/icon.utils';
import AcknowledgedByList from './AcknowledgedByList';
import './assets/AlarmDropdown.css';

interface AlarmDropdownProps {
  alarm: Alarm;
}

export const AlarmDropdown: React.FC<AlarmDropdownProps> = ({ alarm }) => {
  const isDeviceAlarm = useMemo(
    () => alarm.device_locations && alarm.device_locations.length > 0,
    [alarm.device_locations],
  );

  const normalizedPolygon = useMemo(() => {
    if (!isDeviceAlarm && alarm.polygon) {
      return normalizePolygon(alarm.polygon);
    }
    return null;
  }, [alarm.polygon, isDeviceAlarm]);

  const deviceLocations = useMemo(() => {
    if (isDeviceAlarm && alarm.device_locations && alarm.device_locations.length > 0) {
      const deviceCoordinates = alarm.device_locations.map(
        (loc) => [loc.coordinates.lat, loc.coordinates.lng] as [number, number],
      );

      // Calculate bounds if there are multiple device locations, else undefined
      let bounds: undefined | [[number, number], [number, number]] = undefined;
      if (alarm.device_locations.length > 1) {
        bounds = getBoundsFromCoords(deviceCoordinates) ?? undefined;
      }

      return {
        deviceCoordinates,
        bounds,
      };
    }
  }, [alarm.device_locations, isDeviceAlarm]);

  const defaultZoom = useMemo(() => {
    if (isDeviceAlarm && deviceLocations?.deviceCoordinates.length === 1) {
      return 10; // Single device locations require a closer zoom level
    }
    return undefined; // Use bounds to control zoom for multiple device locations or polygon-based alarms
  }, [isDeviceAlarm, deviceLocations]);

  const timeKeys = ['startTime', 'expireTime'].filter(
    (key) => Object.prototype.hasOwnProperty.call(alarm.info || {}, key), // Filters out startTime and expireTime if they are not present in alarm.info
  ) as Array<keyof AlarmInfo>;

  const summaryKeys = ['NWSOffice', 'type'].filter((key) =>
    Object.prototype.hasOwnProperty.call(alarm.info || {}, key),
  ) as Array<keyof AlarmInfo>;

  // Filter out selected keys that are already included in timeKeys and summaryKeys, and sort the remaining keys alphabetically.
  // A key whose value is null or empty draws no row: the API answers every canonical info key on
  // every alarm (null when the alarm's source has no such value), so without this a device alarm
  // would show an empty FEMA identifier, sender and sender name (card BE-29).
  const infoKeys = Object.keys(alarm.info || {})
    .filter((key) => {
      const value = safeGetProp(alarm.info, key as keyof AlarmInfo);
      return value !== null && value !== undefined && value !== '';
    })
    .filter(
      (key) =>
        ![
          'timezone',
          'popaffected',
          'starttime',
          'expiretime',
          'nwsoffice',
          'type',
          'location',
          'latitude',
          'longitude',
          'group_id',
        ].includes(key.toLowerCase()),
    )
    .sort((a, b) => (typeof parseKey === 'function' ? a.localeCompare(b) : a.localeCompare(b)));
  const severity = typeof alarm.info?.severity === 'number' ? alarm.info.severity : 0;

  return (
    <section className="alarm-dropdown w-100 d-flex flex-column gap-3 p-3 user-select-text rounded-3">
      <header className="alarm-dropdown-header d-flex flex-column gap-1 pb-3 border-bottom">
        <h3 className="m-0 fw-semibold fs-5 lh-sm">{withCanceledSuffix(alarm.headline, alarm)}</h3>
      </header>
      <ul className="alarm-dropdown-message m-0 ps-3 d-flex flex-column gap-1">
        {alarm.message
          ?.trim()
          ?.split('*')
          .map((line, index) => (line ? <li key={index}>{line}</li> : null))}
      </ul>
      <dl className="alarm-dropdown-details d-grid gap-3 align-items-stretch m-0">
        {timeKeys.length > 0 && (
          <div
            data-testid="alarm-times"
            className="alarm-dropdown-detail alarm-dropdown-times d-flex flex-column gap-2 p-3 rounded-2 border"
          >
            {timeKeys.map((key) => (
              <div key={key} className="d-flex flex-column gap-1">
                <dt className="alarm-dropdown-label fw-semibold">{parseKey(key)}:</dt>
                <dd className="alarm-dropdown-value m-0">{safeGetProp(alarm.info, key) ?? ''}</dd>
              </div>
            ))}
          </div>
        )}
        {summaryKeys.length > 0 && (
          <div className="alarm-dropdown-stack d-grid gap-2">
            {summaryKeys.map((key) => (
              <div
                key={key}
                className="alarm-dropdown-detail alarm-dropdown-summary d-flex flex-column justify-content-center gap-1 p-2 rounded-2 border"
              >
                <dt className="alarm-dropdown-label fw-semibold">{parseKey(key)}:</dt>
                <dd className="alarm-dropdown-value m-0">
                  {safeGetProp(alarm.info, key as keyof AlarmInfo) ?? ''}
                </dd>
              </div>
            ))}
          </div>
        )}
        {infoKeys.map((key) => (
          <div
            key={key}
            className="alarm-dropdown-detail d-flex flex-column justify-content-center gap-1 p-3 rounded-2 border"
          >
            {key.toLowerCase() !== 'severity' ? (
              <>
                <dt className="alarm-dropdown-label fw-semibold">{parseKey(key)}:</dt>
                <dd className="alarm-dropdown-value m-0">
                  {safeGetProp(alarm.info, key as keyof AlarmInfo) ?? ''}
                </dd>
              </>
            ) : (
              <>
                <dt className="visually-hidden">Alarm level</dt>
                <dd className="m-0">
                  <div className="d-flex flex-lg-row flex-column align-items-lg-center gap-2 h-auto p-0">
                    <div
                      className="alarm-severity-badge d-flex justify-content-center align-items-center rounded-2 gap-2 px-3 fw-semibold"
                      style={{
                        backgroundColor: alertColorScale(severity),
                      }}
                    >
                      {parseKey(key)}: {getSeverityLevel(severity)}
                    </div>
                    <div className="alarm-dropdown-slider rounded-2 bg-base pe-3 flex-grow-1">
                      <CustomSlider
                        title="severity"
                        min={0}
                        max={100}
                        step={1}
                        disabled
                        value={severity}
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </dd>
              </>
            )}
          </div>
        ))}
        <div className="alarm-dropdown-detail d-flex flex-column justify-content-center gap-1 p-3 rounded-2 border">
          <dt className="alarm-dropdown-label fw-semibold">Alarm ID:</dt>
          <dd className="alarm-dropdown-value m-0">{getAlarmIdentifier(alarm)}</dd>
        </div>
      </dl>

      <div className="alarm-dropdown-panels d-grid gap-3 mt-2">
        <div className="alarm-dropdown-panel alarm-dropdown-map p-0 rounded-2 border">
          <BaseMap
            mapCenter={alarm.centroid ? [alarm.centroid.lat, alarm.centroid.lng] : undefined}
            bounds={normalizedPolygon?.bounds || deviceLocations?.bounds}
            scrollZoom={false}
            zoom={defaultZoom}
            disableMapReset={true}
            mapViewHideLabelsToggle={true}
            disableMapControls={true}
          >
            {isDeviceAlarm
              ? deviceLocations?.deviceCoordinates?.map((loc: [number, number], index: number) => (
                  <Marker key={index} position={loc} icon={imageToIcon(rescalert, [30, 30])} />
                ))
              : normalizedPolygon?.polygons.map((poly, index) => (
                  <Polygon key={index} positions={poly} />
                ))}
          </BaseMap>
        </div>
        <div className="alarm-dropdown-panel alarm-dropdown-ack p-3 rounded-2 border">
          <AcknowledgedByList acknowledged={alarm.acknowledged} />
        </div>
      </div>
    </section>
  );
};
